import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Starts every newly visited page at the top.
 *
 * `BrowserRouter` does not do this — only the data routers ship
 * `<ScrollRestoration>` — so the window kept whatever offset the previous page
 * had. From the pricing grid, far down the home page, "Get started" opened
 * /signup scrolled to the same depth; the signup page is much shorter, so the
 * browser clamped to its bottom and the visitor landed on the footer instead of
 * the role choice. Every footer link had the same problem.
 *
 * Only on a new navigation (PUSH/REPLACE). Back and forward are left to the
 * browser's own restoration, which is what React Router's `ScrollRestoration`
 * would do too. Know its limit: the browser restores on `popstate`, while the
 * previous page is still in the DOM, so returning to a long page from a short
 * one (home from /signup) is clamped and lands near the top rather than back
 * on the pricing grid. Fixing that means recording offsets per history entry
 * and restoring after render — deliberately not done here.
 *
 * A URL with a hash is left alone too: an anchor says where to land.
 *
 * `behavior: "instant"` because `html` has `scroll-behavior: smooth`, and a
 * page that visibly scrolls up from its footer on arrival is the bug this fixes
 * wearing a different coat.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP" || hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash, navigationType]);

  return null;
};

export default ScrollToTop;
